import type { AiDecision } from '../../../api/DigiobcanApi';
import {
  AI_DECISION_TYPE_LABEL,
  DRY_RUN_LABEL,
  LABEL_SOURCE,
  aiDecisionResult,
  humanAgreement,
  latency,
  moderationLabel,
  percent,
} from '../../../lib/aiDecisions';
import { shortId } from '../../../lib/ids';
import { __ } from '../../../utils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  StatusChip,
  Typography,
} from '@presov.sk/presov-ui-react';
import { formatDate } from 'date-fns';
import React from 'react';
import { useNavigate } from 'react-router';

type Props = {
  decision: AiDecision | null;
  onClose: () => void;
  /** Zúži zoznam na behy toho istého podnetu. */
  onFilterReport: (reportId: string) => void;
};

const Section: React.FC<React.PropsWithChildren<{ title: string }>> = ({
  title,
  children,
}) => (
  <Stack gap={0.75}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>
      {title.toUpperCase()}
    </Typography>
    {children}
  </Stack>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack direction='row' gap={1.5} alignItems='baseline'>
    <Typography
      sx={{ fontSize: 13, color: 'text.secondary', width: 150, flex: 'none' }}
    >
      {label}
    </Typography>
    <Box sx={{ fontSize: 13, minWidth: 0, overflowWrap: 'anywhere' }}>
      {children}
    </Box>
  </Stack>
);

const Mono: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box component='span' sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
    {children}
  </Box>
);

/**
 * Celé jedno rozhodnutie AI: čo model dostal za úlohu, čo odpovedal, čo sa
 * z toho uplatnilo a ako podnet vyzerá dnes. Všetko prišlo už v riadku
 * zoznamu - dialóg nič nedočítava, preto nemá ani vlastnú adresu.
 *
 * „Dnes" je zámerne vedľa „Uplatnené": rozdiel medzi nimi je presne to, kde
 * úradník AI prepísal, a to je z celého datasetu najcennejšie.
 */
const AiDecisionDialog: React.FC<Props> = ({
  decision,
  onClose,
  onFilterReport,
}) => {
  const navigate = useNavigate();
  const [showRaw, setShowRaw] = React.useState(false);

  React.useEffect(() => setShowRaw(false), [decision?.decisionId]);

  if (!decision) return null;

  const agreement = humanAgreement(decision);
  const { categorization: cat, moderation: mod, duplicates: dup } = decision;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>
        <Stack gap={0.5}>
          <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
            <StatusChip
              tone={decision.type === 'explicit' ? 'info' : 'neutral'}
            >
              {AI_DECISION_TYPE_LABEL[decision.type]}
            </StatusChip>
            {decision.dryRun ? (
              <StatusChip tone='warning'>{DRY_RUN_LABEL}</StatusChip>
            ) : null}
            {decision.error ? (
              <StatusChip tone='error'>{__('Zlyhalo')}</StatusChip>
            ) : null}
            {agreement ? (
              <StatusChip tone={agreement.tone}>{agreement.label}</StatusChip>
            ) : null}
          </Stack>
          <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
            {decision.reportTitle}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {shortId(decision.reportId)} ·{' '}
            {formatDate(new Date(decision.insertedAt), 'dd.MM.yyyy HH:mm:ss')}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack gap={2.5}>
          {decision.dryRun ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {__(
                'Skúšobný beh - na podnete sa nič nezmenilo. „Uplatnilo by sa" je to, čo by AI urobila pri ostrom behu.',
              )}
            </Typography>
          ) : null}
          {decision.error ? (
            <Box
              sx={({ palette, shape }) => ({
                border: `1px solid ${palette.error.light}`,
                borderRadius: `${shape.borderRadius}px`,
                backgroundColor: palette.error.light + '22',
                px: 1.75,
                py: 1.25,
                fontSize: 13,
              })}
            >
              {decision.error}
              <Typography
                sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}
              >
                {decision.dryRun
                  ? __('Skúšobný beh - na podnete sa nič nezmenilo.')
                  : decision.type === 'categories'
                    ? __('Podnet dostal predvolenú kategóriu.')
                    : decision.type === 'duplicates'
                      ? __('Podnet nedostal žiadny návrh duplicity.')
                      : __(
                          'Podnet ostal verejný (pri chybe moderácia nič neskrýva).',
                        )}
              </Typography>
            </Box>
          ) : null}

          <Section title={__('Výsledok')}>
            {cat ? (
              <>
                <Row label={__('Model navrhol')}>
                  {cat.suggested?.name ?? '-'}
                  {cat.confidence !== undefined
                    ? ` (${percent(cat.confidence)})`
                    : ''}
                  {cat.needsManualReview ? (
                    <Box component='span' sx={{ ml: 1 }}>
                      <StatusChip tone='warning'>
                        {__('žiada ručnú kontrolu')}
                      </StatusChip>
                    </Box>
                  ) : null}
                </Row>
                <Row
                  label={
                    decision.dryRun ? __('Uplatnilo by sa') : __('Uplatnené')
                  }
                >
                  {cat.applied?.name ?? '-'}
                </Row>
                <Row label={__('Dnes')}>
                  {cat.current.length
                    ? cat.current.map((c) => c.name).join(', ')
                    : __('žiadny nález')}
                </Row>
                {cat.alternatives.length ? (
                  <Row label={__('Alternatívy')}>
                    <Stack gap={0.25}>
                      {cat.alternatives.map((a, index) => (
                        <span key={`${a.categoryId}-${index}`}>
                          {a.name ?? a.categoryId ?? __('neznáma kategória')} (
                          {percent(a.score)})
                        </span>
                      ))}
                    </Stack>
                  </Row>
                ) : null}
              </>
            ) : null}

            {mod ? (
              <>
                <Row label={__('Verdikt')}>
                  {mod.explicit ? __('Skryť') : __('Verejné')}
                  {mod.linked ? (
                    <Typography
                      component='span'
                      sx={{ fontSize: 12, color: 'text.secondary', ml: 1 }}
                    >
                      {__('(tento beh stav podnetu naposledy nastavil)')}
                    </Typography>
                  ) : null}
                </Row>
                <Row label={__('Dnes')}>
                  {mod.currentExplicit ? __('Skrytý') : __('Verejný')}
                </Row>
                <Row label={__('Štítky')}>
                  {mod.labels.length ? (
                    <Stack direction='row' gap={0.75} flexWrap='wrap'>
                      {mod.labels.map((label) => (
                        <StatusChip key={label.label} tone='neutral'>
                          {moderationLabel(label.label)}
                          {label.score !== undefined
                            ? ` ${percent(label.score)}`
                            : ''}
                          {label.source
                            ? ` · ${LABEL_SOURCE[label.source] ?? label.source}`
                            : ''}
                        </StatusChip>
                      ))}
                    </Stack>
                  ) : (
                    __('nič nevhodné')
                  )}
                </Row>
                {mod.spans.length ? (
                  <Row label={__('Citácie')}>
                    <Stack gap={0.25}>
                      {mod.spans.map((span, index) => (
                        <span key={index}>
                          „{span.text}"
                          {span.label ? (
                            <Typography
                              component='span'
                              sx={{ fontSize: 12, color: 'text.secondary' }}
                            >
                              {' '}
                              · {moderationLabel(span.label)}
                            </Typography>
                          ) : null}
                        </span>
                      ))}
                    </Stack>
                  </Row>
                ) : null}
              </>
            ) : null}

            {dup ? (
              <>
                <Row label={__('Fotky')}>
                  {dup.mediaCount
                    ? `${dup.mediaCount} ${__('s embeddingom')}`
                    : __('žiadna s embeddingom - detektor nemal z čoho hľadať')}
                </Row>
                <Row label={__('Návrhy')}>
                  {dup.candidates.length ? (
                    <Stack gap={0.25}>
                      {/* Vzdialenosť je L2 medzi embeddingmi najpodobnejšej
                          dvojice fotiek - menšia znamená podobnejšie. */}
                      {dup.candidates.map((c, index) => (
                        <span key={`${c.reportId}-${c.categoryId}-${index}`}>
                          {c.reportTitle ?? __('podnet už neexistuje')}{' '}
                          <Mono>{shortId(c.reportId)}</Mono>
                          <Typography
                            component='span'
                            sx={{ fontSize: 12, color: 'text.secondary' }}
                          >
                            {c.categoryName ? ` · ${c.categoryName}` : ''}
                            {` · ${__('vzdialenosť')} ${c.distance.toLocaleString(
                              'sk-SK',
                              { maximumFractionDigits: 3 },
                            )}`}
                          </Typography>
                        </span>
                      ))}
                    </Stack>
                  ) : dup.mediaCount ? (
                    __('žiadny podobný podnet')
                  ) : (
                    '-'
                  )}
                </Row>
                <Row label={__('Dnes')}>
                  {dup.merged
                    ? __('zlúčený ako duplikát')
                    : dup.pending
                      ? __('návrh čaká na rozhodnutie úradníka')
                      : dup.candidates.length
                        ? __('návrhy úradník zamietol')
                        : __('nezlúčený')}
                </Row>
              </>
            ) : null}

            {!cat && !mod && !dup ? aiDecisionResult(decision) : null}
          </Section>

          {decision.reason ? (
            <Section title={__('Zdôvodnenie modelu')}>
              <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>
                {decision.reason}
              </Typography>
            </Section>
          ) : null}

          <Section title={__('Beh')}>
            <Row label={__('Model')}>
              <Mono>{decision.model}</Mono>
            </Row>
            {decision.modelVersion ? (
              <Row label={__('Verzia modelu')}>
                <Mono>{decision.modelVersion}</Mono>
              </Row>
            ) : null}
            {/* Duplicity hľadá embedding a pgvector, nie prompt - verzia
                je verzia postupu (hranice vzdialenosti, veku a počtu). */}
            <Row label={dup ? __('Verzia postupu') : __('Verzia promptu')}>
              <Mono>{decision.promptVersion}</Mono>
            </Row>
            <Row label={__('Latencia')}>
              {latency(decision.latencyMs) ?? '-'}
            </Row>
            {cat?.taxonomyHash ? (
              <Row label={__('Taxonómia')}>
                <Mono>{cat.taxonomyHash.slice(0, 12)}</Mono>
              </Row>
            ) : null}
          </Section>

          {decision.rawOutput ? (
            <Section
              title={
                dup ? __('Parametre hľadania') : __('Surový výstup modelu')
              }
            >
              {showRaw ? (
                <Box
                  component='pre'
                  sx={({ palette, shape }) => ({
                    m: 0,
                    p: 1.5,
                    fontSize: 12,
                    maxHeight: 320,
                    overflow: 'auto',
                    backgroundColor: palette.grey[50],
                    border: `1px solid ${palette.divider}`,
                    borderRadius: `${shape.borderRadius}px`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  })}
                >
                  {decision.rawOutput}
                </Box>
              ) : (
                <Box>
                  <Button variant='text' onClick={() => setShowRaw(true)}>
                    {__('Zobraziť JSON')}
                  </Button>
                </Box>
              )}
            </Section>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant='text'
          onClick={() => {
            onFilterReport(decision.reportId);
            onClose();
          }}
        >
          {__('Všetky behy podnetu')}
        </Button>
        <Button
          variant='text'
          onClick={() =>
            navigate(`/administracia/podnety/${shortId(decision.reportId)}`)
          }
        >
          {__('Otvoriť podnet')}
        </Button>
        <Button onClick={onClose}>{__('Zavrieť')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiDecisionDialog;
